import Vue from 'vue';
import {
  formatNumber,
  alignCells,
  getNodeChildDetails,
  LinkCellRenderer,
  autoResize,
  ProgressBarByFieldsRenderer,
  addGroupingInResponseData,
  booleanRenderer
} from '@/helpers/renderHelpers';
import {
  addColIdToColumns,
  handlerResponse,
} from '@/helpers/commonHelpers';
import { CellRendererService } from '@/helpers/renderService'

import gridProps from '@/gridProps';
const RenderService = new CellRendererService()

export default {
  namespaced: true,
  state: {
    comments: [],
    documents: [],
    gridOptions: {
      animateRows: true,
      rowSelection: 'multiple',
      getNodeChildDetails: getNodeChildDetails,
      groupSelectsChildren: false,
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        valueFormatter(params) {
          return formatNumber(params.value, null)
        },
        cellStyle(params) {
          return alignCells(params);
        }
      },
      onRowDataChanged() {
        if (this.rowData && this.rowData.length) {
          autoResize(this);
        } else {
          this.api && this.api.hideOverlay();
          this.api && this.api.showNoRowsOverlay();
        }
      }
    },
    salesOrderItemsGrid: {},
    salesOrderItemsGridName: '',
    salesContractsShort: [],
    salesOrderItems: [],
    salesOrderItemsColumns: [],
    shipments: [],
    shipmentsColumns: [],
    packagesGroupingByOrderItems: [],
    packagesGroupingByOrderItemsColumns: [],
    nonBoundedPackages: [],
    nonBoundedPackagesColumns: []
  },
  getters: {
    salesContractsShort(state) {
      return state.salesContractsShort;
    },
    salesOrderItemsGrid(state) {
      return {
        ...state.gridOptions, ...gridProps,
        ...{
          components: {
            'ReadinessProgressBar': ProgressBarByFieldsRenderer('shipped_m3', 'ordered_m3'),
            'ReadinessProgressBarM3': ProgressBarByFieldsRenderer('reserved_product_m3', 'unshipped_m3', true),
            'Activity': booleanRenderer({ trueValue: 'active', falseValue: 'inactive'}),
            'exclamatoryMark': RenderService.exclamatoryMark()
          }
        },
        isRowSelectable(rowNode) {
          return rowNode.data ? rowNode.data.id : false
        }
      }
    },
    packagesGroupingByOrderItemsGrid(state) {
      return {...state.gridOptions};
    },
    nonBoundedPackagesGrid(state) {
      return {...state.gridOptions};
    },
    shipmentsGrid(state) {
      return {
        ...state.gridOptions, ...{
          groupSelectsChildren: true,
          components: {
            'InvoiceLink': LinkCellRenderer('invoice_item__invoice_id', '/sales-invoice/'),
            'ShipmentLink': LinkCellRenderer('id', '/operation/'),
            'ReadinessProgressBar': ProgressBarByFieldsRenderer('invoiced_packages_count', 'shipped_packages_count')
          }
        }
      };
    },
    salesOrderItemsColumns(state) {
      return state.salesOrderItemsColumns;
    },
    salesOrderItems(state) {
      return state.salesOrderItems;
    },
    shipments(state) {
      return state.shipments;
    },
    shipmentsColumns(state) {
      return state.shipmentsColumns;
    },
    packagesGroupingByOrderItems(state) {
      return state.packagesGroupingByOrderItems;
    },
    packagesGroupingByOrderItemsColumns(state) {
      return state.packagesGroupingByOrderItemsColumns;
    },
    nonBoundedPackages(state) {
      return state.nonBoundedPackages;
    },
    nonBoundedPackagesColumns(state) {
      return state.nonBoundedPackagesColumns;
    },
    comments(state) {
      return state.comments;
    },
    documents(state) {
      return state.documents;
    }
  },
  mutations: {
    setSalesContractsShort(state, data) {
      state.salesContractsShort = data;
    },
    setSalesOrderItems(state, payLoad) {
      if (payLoad.data) {
        state.salesOrderItems = addGroupingInResponseData(payLoad.data);
      } else {
        state.salesOrderItems = payLoad;
      }
      state.salesOrderItemsColumns = addColIdToColumns(payLoad.used_columns);
      state.salesOrderItemsGridName = payLoad.grid_name;
    },
    setShipments(state, payLoad) {
      if (payLoad.data) {
        state.shipments = addGroupingInResponseData(payLoad.data);
      } else {
        state.shipments = payLoad;
      }
      state.shipmentsColumns = addColIdToColumns(payLoad.used_columns);
    },
    setPackagesGroupingByOrderItems(state, payLoad) {
      if (payLoad.data) {
        state.packagesGroupingByOrderItems = addGroupingInResponseData(payLoad.data);
      } else {
        state.packagesGroupingByOrderItems = payLoad;
      }
      state.packagesGroupingByOrderItemsColumns = addColIdToColumns(payLoad.used_columns);
    },
    setNonBoundedPackages(state, payLoad) {
      state.nonBoundedPackages = payLoad.data;
      state.nonBoundedPackagesColumns = addColIdToColumns(payLoad.used_columns);
    },
    setCommentsList(state, data) {
      state.comments = data;
    },
    setDocumentsList(state, data) {
      state.documents = data;
    }
  },
  actions: {
    loadSalesContractsShort(store, payLoad) {
      const params = {
        short_view: true, statuses: 'active', for_sales_order: payLoad.id
      };
      Vue.http.get(`sales/sales_contracts/`, {params})
        .then(handlerResponse)
        .then(data => {
          store.commit('setSalesContractsShort', data);
        });
    },
    loadSalesOrderItems(store, payLoad) {
      Vue.http.get(`sales/salesorders/${payLoad.id}/salesordersitems/`)
        .then(handlerResponse)
        .then(data => {
          store.commit('setSalesOrderItems', data);
        });
    },
    loadShipments(store, payLoad) {
      Vue.http.get(`sales/salesorders/${payLoad.id}/movements_with_bound_packages/`)
        .then(handlerResponse)
        .then(data => {
          store.commit('setShipments', data);
        });
    },
    loadPackagesGroupingByOrderItems(store, payLoad) {
      Vue.http.get(`sales/salesorders/${payLoad.id}/salesordersitems/movementdata/`)
        .then(handlerResponse)
        .then(data => {
          store.commit('setPackagesGroupingByOrderItems', data);
        });
    },
    loadNonBoundedPackages(store, payLoad) {
      Vue.http.get(`sales/salesorders/${payLoad.id}/free_movementdata/`)
        .then(handlerResponse)
        .then(data => {
          store.commit('setNonBoundedPackages', data);
        });
    },
    loadInvoiceListGroupingByOrders(store, payLoad) {
      Vue.http.get(`sales/sales_contracts/${payLoad.id}/sales_orders/`)
        .then(handlerResponse)
        .then(data => {
          store.commit('setInvoiceListGroupingByOrders', data);
        });
    },
    loadCommentsList(store, payLoad) {
      Vue.http.get(`sales/salesorders/${payLoad.id}/comments/`)
        .then(handlerResponse)
        .then(data => {
          store.commit('setCommentsList', data);
        });
    },
    loadDocumentsList(store, payLoad) {
      Vue.http.get(`sales/salesorders/${payLoad.id}/attachments/`)
        .then(handlerResponse)
        .then(data => {
          store.commit('setDocumentsList', data);
        });
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/sales/orders/salesOrder.js